import { useLocation, useParams } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";

import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";

import { ArrowLeft, Building2, Factory, Search, Sprout, Truck, X } from "lucide-react";
import { Badge } from "../../../components/ui/common/badge";
import { Button } from "../../../components/ui/common/button";
import { Card, CardContent } from "../../../components/ui/common/card";
import { ApiFarmGate, ApiProcessGate } from "../../../types/prodcut-data-review/product-data.types";
import ExternalTransportTable from "./transportRecords/components/ExternalTransportTable";
import GateCard from "./gate/components/GateCard";
import GateCardContent from "./gate/components/GateCardContent";
import StorageTable from "./storageRecords/StorageTable";
import { useDrawingToolLock } from "./hooks/useDrawingToolLock";
import { useGateSearch } from "./hooks/useGateSearch";
import { useReviewGateData } from "./hooks/useReviewGateData";

const MappingDataReviewNew = () => {
  const { drawingId, ownerId, sourceId } = useParams();
  const { mappingType: paramMappingType } = useParams<string>();
  const mappingType = paramMappingType as keyof typeof MappingSourceType;
  const mappingSourceType = MappingSourceType[mappingType];
  const queryParams = new URLSearchParams(useLocation().search);
  const isAverageProduct = queryParams.get("isAverageProduct") === "true";

  const { isDrawingToolLoading, isLocked, isLoadingImageUpload, isLoadingPutDataToQueue, cancel } =
    useDrawingToolLock({ mappingSourceType, sourceId, ownerId, isAverageProduct });

  const {
    farmGates,
    processGates,
    externalStorages,
    externalTransports,
    transportTypeMap,
    refetchReviewData,
    handleGateChange,
    handleProcessGateChange,
    saveExternalStorage,
    saveExternalTransport
  } = useReviewGateData({ drawingId });

  const { q, setQ, filteredFarmGates, filteredProcessGates } = useGateSearch(farmGates, processGates);

  if (isDrawingToolLoading || isLoadingImageUpload || isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <div className="tw-p-6 tw-w-full tw-space-y-6">
      {/* Header */}
      <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-end md:tw-justify-between">
        <div className="tw-flex tw-gap-6 tw-items-center">
          <Button variant="outline" size="icon" className="tw-h-12 tw-w-20" onClick={cancel} title="Back">
            <ArrowLeft className="tw-h-3.5 tw-w-3.5" />
            Back
          </Button>
          <div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-foreground">Data Review</h1>
            <p className="tw-text-sm tw-text-muted-foreground">
              Review and update lifecycle data across all gates.
            </p>
          </div>
        </div>
        <div className="tw-relative tw-w-full md:tw-w-80">
          <Search className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground tw-pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search farm or process gates..."
            className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-pl-9 tw-pr-8 tw-text-xs tw-shadow-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-ring"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground hover:tw-text-foreground"
              aria-label="Clear search"
            >
              <X className="tw-h-4 tw-w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Farm Gates */}
      <section className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Sprout className="tw-h-4 tw-w-4 tw-text-primary" />
          <h2 className="tw-text-sm tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide">
            Farm Gates
          </h2>
          <Badge variant="secondary" className="tw-text-xs">
            {filteredFarmGates.length}
            {q && ` / ${farmGates.length}`}
          </Badge>
        </div>
        {filteredFarmGates.length === 0 ? (
          <Card className="tw-border-dashed tw-border-2">
            <CardContent className="tw-py-10 tw-text-center tw-text-sm tw-text-muted-foreground">
              {q ? `No farm gates match "${q}".` : "No farm gates."}
            </CardContent>
          </Card>
        ) : (
          filteredFarmGates.map(gate => {
            const count =
              (gate.cultivationField ? 1 : 0) +
              (gate.processing?.length ?? 0) +
              (gate.packaging?.length ?? 0) +
              (gate.internalStorage?.length ?? 0) +
              (gate.internalTransportation?.length ?? 0);
            return (
              <GateCard key={gate._id} icon={Sprout} title={gate.farmGateName} count={count}>
                <GateCardContent
                  kind="farm"
                  gate={gate as any}
                  onChange={next => handleGateChange(gate._id, next as unknown as ApiFarmGate)}
                  ownerId={ownerId ?? ""}
                  sourceId={sourceId ?? ""}
                  refetchReviewData={() => refetchReviewData()}
                  disabled={isLocked}
                  transportTypeMap={transportTypeMap}
                />
              </GateCard>
            );
          })
        )}
      </section>

      {/* Process Gates */}
      <section className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Factory className="tw-h-4 tw-w-4 tw-text-primary" />
          <h2 className="tw-text-sm tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide">
            Process Gates
          </h2>
          <Badge variant="secondary" className="tw-text-xs">
            {filteredProcessGates.length}
            {q && ` / ${processGates.length}`}
          </Badge>
        </div>
        {filteredProcessGates.length === 0 ? (
          <Card className="tw-border-dashed tw-border-2">
            <CardContent className="tw-py-10 tw-text-center tw-text-sm tw-text-muted-foreground">
              {q ? `No process gates match "${q}".` : "No process gates."}
            </CardContent>
          </Card>
        ) : (
          filteredProcessGates.map(gate => {
            const count =
              (gate.processing?.length ?? 0) +
              (gate.packaging?.length ?? 0) +
              (gate.internalStorage?.length ?? 0) +
              (gate.internalTransportation?.length ?? 0);
            return (
              <GateCard key={gate._id} icon={Factory} title={gate.processGateName} count={count}>
                <GateCardContent
                  kind="process"
                  gate={gate as any}
                  onChange={next =>
                    handleProcessGateChange(gate._id, next as unknown as ApiProcessGate)
                  }
                  ownerId={ownerId ?? ""}
                  sourceId={sourceId ?? ""}
                  disabled={isLocked}
                  transportTypeMap={transportTypeMap}
                />
              </GateCard>
            );
          })
        )}
      </section>

      {/* External Warehouses */}
      <section className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Building2 className="tw-h-4 tw-w-4 tw-text-primary" />
          <h2 className="tw-text-sm tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide">
            External Warehouses
          </h2>
          <Badge variant="secondary" className="tw-text-xs">
            {externalStorages.length}
          </Badge>
        </div>
        <Card>
          <CardContent className="tw-pt-4">
            {externalStorages.length === 0 ? (
              <p className="tw-text-xs tw-text-muted-foreground tw-italic tw-px-2 tw-py-3">
                No external warehouses.
              </p>
            ) : (
              <StorageTable
                label="external warehouses"
                records={externalStorages}
                onSave={saveExternalStorage}
                disabled={isLocked}
              />
            )}
          </CardContent>
        </Card>
      </section>

      {/* External Transportation */}
      <section className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Truck className="tw-h-4 tw-w-4 tw-text-primary" />
          <h2 className="tw-text-sm tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide">
            External Transportation
          </h2>
          <Badge variant="secondary" className="tw-text-xs">
            {externalTransports.length}
          </Badge>
        </div>
        <Card>
          <CardContent className="tw-pt-4">
            {externalTransports.length === 0 ? (
              <p className="tw-text-xs tw-text-muted-foreground tw-italic tw-px-2 tw-py-3">
                No external transportation.
              </p>
            ) : (
              <ExternalTransportTable
                records={externalTransports}
                onSave={saveExternalTransport}
                disabled={isLocked}
              />
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default MappingDataReviewNew;
